import { MY_VCS_USERNAME_TICKET_OWNER } from './../selectors/memoizedSelectors';
import { displayCodeStatusForStatus } from '../helpers/displayHelpers';
import { FeatureItemCodeStatus, PullRequestMergeQueueState } from '../types';
import { allCases } from '../typescriptHelpers';

export enum FilterType {
    TicketOwner = 'ticketOwner',
    FirstSeenInRCBuild = 'firstSeenInRCBuild',
    HideSubtasks = 'hideSubtaskTickets',
    HideItemsWithMissingFAs = 'hideMissingFeatureAffiliations',
    HideItemsWithTickets = 'hideItemsWithTickets',
    HideUnsquashedChildCommits = 'hideUnsquashedChildCommits',
    HideDraftPRs = 'hideDraftPRs',
    CodeAuthor = 'codeAuthor',
    CodeStatus = 'codeStatus',
    CodeFA = 'codeFA',
    TicketProject = 'ticketProject',
    TicketStatus = 'ticketStatus',
    TicketCustomField = 'ticketCustomField-', // trailing hyphen is separator when custom field name is appended for keys
    TicketPriority = 'ticketPriority',
    TicketType = 'ticketType',
    TicketFA = 'ticketFA',
    AssociatedIssues = 'associatedIssues',
    ShowFixesOnly = 'showFixesOnly',
}

export type FilterTypeOrCustomField =
    | Exclude<FilterType, FilterType.TicketCustomField>
    | `${FilterType.TicketCustomField}${string}`;

export const AllFilterTypes = allCases(FilterType);

export interface Filter {
    type: FilterType;
}

export interface TicketOwnerFilter extends Filter {
    type: FilterType.TicketOwner;
    ticketOwner: string;
}

export interface TicketFAFilter extends Filter {
    type: FilterType.TicketFA;
    ticketFA: string;
}

export interface CodeAuthorFilter extends Filter {
    type: FilterType.CodeAuthor;
    codeAuthor: string;
}

export interface CodeFAFilter extends Filter {
    type: FilterType.CodeFA;
    codeFA: string;
}

export interface HideSubtasksFilter extends Filter {
    type: FilterType.HideSubtasks;
    hideSubtasks: boolean;
}

export interface HideUnsquashedChildCommitsFilter extends Filter {
    type: FilterType.HideUnsquashedChildCommits;
    hideUnsquashedChildCommits: boolean;
}

export interface HideDraftPRsFilter extends Filter {
    type: FilterType.HideDraftPRs;
    hideDraftPRs: boolean;
}

export type CodeStatusFilterValue = FeatureItemCodeStatus | PullRequestMergeQueueState;

export interface CodeStatusFilter extends Filter {
    type: FilterType.CodeStatus;
    codeStatus: CodeStatusFilterValue;
}

export interface TicketProjectFilter extends Filter {
    type: FilterType.TicketProject;
    ticketProjectId: string;
    ticketProjectDisplayName: string;
}

export interface OnlyShowItemsWithFAsFilter extends Filter {
    type: FilterType.HideItemsWithMissingFAs;
    hideMissingFeatureAffiliations: boolean;
}

export interface OnlyShowItemsWithoutTickets extends Filter {
    type: FilterType.HideItemsWithTickets;
    hideItemsWithTickets: boolean;
}

export interface OnlyShowFixesOnly extends Filter {
    type: FilterType.ShowFixesOnly;
    showFixesOnly: boolean;
}

export interface TicketStatusFilter extends Filter {
    type: FilterType.TicketStatus;
    status: string;
}

export interface TicketCustomFieldFilter extends Filter {
    type: FilterType.TicketCustomField;
    key: `${FilterType.TicketCustomField}${string}`;
    value: string;
}

export interface TicketPriorityFilter extends Filter {
    type: FilterType.TicketPriority;
    priority: string;
}

export interface TicketTypeFilter extends Filter {
    type: FilterType.TicketType;
    ticketType: string;
}

export interface AssociatedIssuesFilter extends Filter {
    type: FilterType.AssociatedIssues;
    associatedIssueId: string;
    associatedIssueShortDescription: string;
    buildId: string;
    isFilteredByBuild: boolean;
}

export interface FirstSeenInRCBuildFilter extends Filter {
    type: FilterType.FirstSeenInRCBuild;
    buildIdentifier: string;
}

export type AnyFilter =
    | TicketOwnerFilter
    | HideSubtasksFilter
    | HideUnsquashedChildCommitsFilter
    | HideDraftPRsFilter
    | OnlyShowItemsWithFAsFilter
    | OnlyShowItemsWithoutTickets
    | OnlyShowFixesOnly
    | CodeAuthorFilter
    | CodeStatusFilter
    | CodeFAFilter
    | TicketProjectFilter
    | TicketStatusFilter
    | TicketPriorityFilter
    | TicketTypeFilter
    | TicketFAFilter
    | AssociatedIssuesFilter
    | FirstSeenInRCBuildFilter
    | TicketCustomFieldFilter;

export const shortDescriptionForFilter: (filter: Filter, useWorkingBranch: boolean, vcsUsers: string[]) => string = (
    filter,
    useWorkingBranch,
    vcsUsers,
) => {
    switch (filter.type) {
        case FilterType.HideItemsWithMissingFAs:
            return 'Only show tickets with FAs applied';
        case FilterType.HideItemsWithTickets:
            return 'Only show items missing tickets';
        case FilterType.HideSubtasks:
            return 'Hide subtasks';
        case FilterType.HideUnsquashedChildCommits:
            return 'Hide unsquashed commits from merged PRs';
        case FilterType.HideDraftPRs:
            return 'Hide draft PRs';
        case FilterType.ShowFixesOnly:
            return 'Show fixes only';
        case FilterType.TicketOwner: {
            const ticketOwnerFilter = filter as TicketOwnerFilter;
            return `Ticket owner: ${ticketOwnerFilter.ticketOwner}`;
        }
        case FilterType.CodeAuthor: {
            const codeAuthorFilter = filter as CodeAuthorFilter;
            if (codeAuthorFilter.codeAuthor === MY_VCS_USERNAME_TICKET_OWNER) {
                return `Commit or PR author: My user (${
                    vcsUsers.length > 0 ? vcsUsers.join(', ') : 'No associated VCS user found'
                })`;
            }
            return `Commit or PR author: ${codeAuthorFilter.codeAuthor}`;
        }
        case FilterType.CodeStatus: {
            const codeStatusFilter = filter as CodeStatusFilter;
            return `Code status: ${displayCodeStatusForStatus(codeStatusFilter.codeStatus, useWorkingBranch)}`;
        }
        case FilterType.CodeFA: {
            const codeFAFilter = filter as CodeFAFilter;
            return `PR label/milestone: ${codeFAFilter.codeFA}`;
        }
        case FilterType.TicketFA: {
            const ticketFAFilter = filter as TicketFAFilter;
            return `Ticket feature affiliation: ${ticketFAFilter.ticketFA}`;
        }
        case FilterType.TicketProject: {
            const ticketProjectFilter = filter as TicketProjectFilter;

            return `Ticket project: ${ticketProjectFilter.ticketProjectDisplayName}`;
        }
        case FilterType.TicketStatus: {
            const ticketStatusFilter = filter as TicketStatusFilter;

            return `Ticket status: ${ticketStatusFilter.status}`;
        }
        case FilterType.TicketPriority: {
            const ticketPriorityFilter = filter as TicketPriorityFilter;

            return `Ticket priority: ${ticketPriorityFilter.priority}`;
        }
        case FilterType.TicketType: {
            const ticketTypeFilter = filter as TicketTypeFilter;

            return `Ticket type: ${ticketTypeFilter.ticketType}`;
        }
        case FilterType.AssociatedIssues: {
            const associatedIssuesFilter = filter as AssociatedIssuesFilter;

            return `Associated issue: ${associatedIssuesFilter.associatedIssueShortDescription}`;
        }
        case FilterType.FirstSeenInRCBuild: {
            const firstSeenFilter = filter as FirstSeenInRCBuildFilter;

            return `Item is new in RC build: #${firstSeenFilter.buildIdentifier}`;
        }
        case FilterType.TicketCustomField: {
            const customFieldFilter = filter as TicketCustomFieldFilter;

            return ticketCustomFieldNameFromFilter(customFieldFilter) + ': ' + customFieldFilter.value;
        }
    }
};

export const ticketCustomFieldNameFromFilter = (filter: TicketCustomFieldFilter): string => {
    return filter.key.replace(FilterType.TicketCustomField, '');
};
